import { type BranchGroupSummary } from '../lib/api';
import { formatDuration } from '../lib/format';

interface Props {
  branches: BranchGroupSummary[];
}

export function BranchBreakdown({ branches }: Props) {
  if (branches.length === 0) return null;

  const max = Math.max(...branches.map((b) => b.total_seconds), 1);

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-sub font-medium tracking-sub text-black">
          Per-branch breakdown
        </h2>
        <span className="mono-label text-black/50">
          {branches.length} {branches.length === 1 ? 'branch' : 'branches'}
        </span>
      </div>
      <div className="card overflow-hidden">
        <ul className="divide-y divide-border-light">
          {branches.map((b) => {
            const pct = Math.round((b.total_seconds / max) * 100);
            return (
              <li
                key={b.branch}
                className="px-5 py-4 hover:bg-glass-dark transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`badge ${
                        b.branch === 'unknown'
                          ? '!bg-[#f5f5f5] !border-[#e0e0e0] !text-black/50'
                          : '!bg-[#e8f4ff] !border-[#bdd9ff] !text-midnight'
                      } font-mono`}
                    >
                      {b.branch}
                    </span>
                    <span className="mono-label text-black/50 shrink-0">
                      {b.block_count} {b.block_count === 1 ? 'block' : 'blocks'}
                    </span>
                  </div>
                  <span className="font-display text-feature font-medium tracking-feature text-black tabular-nums shrink-0">
                    {formatDuration(b.total_seconds)}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-1 bg-glass-dark rounded overflow-hidden">
                    <div
                      className="h-full bg-midnight"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mono-label text-black/40 tabular-nums shrink-0 flex gap-2">
                    <span>human {formatDuration(b.human_seconds)}</span>
                    <span>·</span>
                    <span>agent {formatDuration(b.agent_seconds)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
